import type { BillingPreviewUI, DiscountEvaluation, SavingsSummary } from "@/domain/billing/billing-preview.model"

function num(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v)
}

/** Current backend payload inside HTTP envelope `data`. */
export type BillingPreviewApiPayload = {
  sub_total?: number
  total_tax?: number
  total_fees?: number
  total_discount?: number
  payable_amount?: number
  taxes?: BillingPreviewApiTaxRow[]
  fees?: BillingPreviewApiFeeRow[]
  discounts?: BillingPreviewApiDiscountRow[]
  savings_summary?: BillingPreviewApiSavingsSummary | null
  discount_evaluations?: unknown
}

export type BillingPreviewApiSavingsSummary = {
  menu_offer_savings?: number
  coupon_savings?: number
  total_savings?: number
}

export type BillingPreviewApiTaxRow = {
  tax_name?: string
  tax_applied_amount?: number
}

export type BillingPreviewApiFeeRow = {
  fee_name?: string
  fee_applied_amount?: number
}

export type BillingPreviewApiDiscountRow = {
  discount_name?: string
  discount_applied_amount?: number
}

/** Previous API shape (legacy) — still mapped when `summary` is present. */
export type BillingPreviewLegacySummary = {
  sub_total?: number
  total_tax?: number
  total_fees?: number
  total_discount?: number
  payable_amount?: number
}

export type BillingPreviewLegacyPayload = {
  summary?: BillingPreviewLegacySummary
  appliedDiscountList?: unknown[]
  appliedChargesList?: unknown[]
  savings_summary?: BillingPreviewApiSavingsSummary | null
  discount_evaluations?: unknown
}

/** Axios/fetch body for POST billing preview: `{ success, resultCode, message, data }`. */
export type BillingPreviewHttpEnvelope = {
  success?: boolean
  resultCode?: number
  message?: string
  data?: BillingPreviewApiPayload | BillingPreviewLegacyPayload | null
}

function mapTaxRows(rows: unknown): BillingPreviewUI["taxes"] {
  if (!Array.isArray(rows)) return []
  return rows.map((row) => {
    const o = isRecord(row) ? row : {}
    return {
      name: String(o.tax_name ?? "Tax"),
      amount: num(o.tax_applied_amount),
    }
  })
}

function mapFeeRows(rows: unknown): BillingPreviewUI["fees"] {
  if (!Array.isArray(rows)) return []
  return rows.map((row) => {
    const o = isRecord(row) ? row : {}
    return {
      name: String(o.fee_name ?? "Fee"),
      amount: num(o.fee_applied_amount),
    }
  })
}

function mapDiscountRows(rows: unknown): BillingPreviewUI["discounts"] {
  if (!Array.isArray(rows)) return []
  return rows.map((row) => {
    const o = isRecord(row) ? row : {}
    return {
      name: o.discount_name != null ? String(o.discount_name) : undefined,
      amount: num(o.discount_applied_amount),
    }
  })
}

function mapLegacyDiscountList(list: unknown): BillingPreviewUI["discounts"] {
  if (!Array.isArray(list)) return []
  const out: BillingPreviewUI["discounts"] = []
  for (const row of list) {
    if (!isRecord(row)) continue
    out.push({
      name: row.discount_name != null ? String(row.discount_name) : undefined,
      amount: num(row.discount_applied_amount ?? row.amount),
    })
  }
  return out
}

function mapLegacyChargesList(list: unknown): BillingPreviewUI["fees"] {
  if (!Array.isArray(list)) return []
  const out: BillingPreviewUI["fees"] = []
  for (const row of list) {
    if (!isRecord(row)) continue
    out.push({
      name: String(row.charge_name ?? row.fee_name ?? row.name ?? "Fee"),
      amount: num(row.charge_applied_amount ?? row.fee_applied_amount ?? row.amount),
    })
  }
  return out
}

/**
 * Maps optional `discount_evaluations` array. Returns `undefined` when absent.
 * Invalid rows are skipped (never throws).
 */
export function mapDiscountEvaluations(raw: unknown): DiscountEvaluation[] | undefined {
  if (raw === undefined || raw === null) return undefined
  if (!Array.isArray(raw)) return undefined

  const out: DiscountEvaluation[] = []
  for (const row of raw) {
    if (!isRecord(row)) continue
    const statusRaw = String(row.status ?? "").trim().toLowerCase()
    if (statusRaw !== "applied" && statusRaw !== "rejected") continue

    const evalRow: DiscountEvaluation = {
      status: statusRaw,
    }

    if (row.discount_id != null && String(row.discount_id).trim() !== "") {
      evalRow.discount_id = String(row.discount_id)
    }
    if (row.applied_amount != null && Number.isFinite(Number(row.applied_amount))) {
      evalRow.applied_amount = Number(row.applied_amount)
    }
    if (row.reason_code != null && String(row.reason_code).trim() !== "") {
      evalRow.reason_code = String(row.reason_code)
    }
    if (row.reason_message != null && String(row.reason_message).trim() !== "") {
      evalRow.reason_message = String(row.reason_message)
    }
    if (row.minimum_order_amount != null && Number.isFinite(Number(row.minimum_order_amount))) {
      evalRow.minimum_order_amount = Number(row.minimum_order_amount)
    }
    if (row.amount_remaining != null && Number.isFinite(Number(row.amount_remaining))) {
      evalRow.amount_remaining = Number(row.amount_remaining)
    }

    out.push(evalRow)
  }
  return out
}

/**
 * Structural map of backend `savings_summary` → {@link SavingsSummary}.
 * Returns `null` when the object is absent. Does not calculate or fall back to `total_discount`.
 */
export function mapSavingsSummary(raw: unknown): SavingsSummary | null {
  if (!isRecord(raw)) return null
  return {
    menuOfferSavings: num(raw.menu_offer_savings),
    couponSavings: num(raw.coupon_savings),
    totalSavings: num(raw.total_savings),
  }
}

/**
 * Maps raw `data` object from the billing preview HTTP envelope to `BillingPreviewUI`.
 * Supports both the new flat shape and legacy `{ summary, appliedChargesList, appliedDiscountList }`.
 */
export function mapBillingPreviewApiPayloadToUI(data: unknown): BillingPreviewUI {
  if (!isRecord(data)) {
    throw new Error("Invalid billing preview response")
  }

  const savingsSummary = mapSavingsSummary(data.savings_summary)
  const discountEvaluations = mapDiscountEvaluations(data.discount_evaluations)

  const legacySummary = data.summary
  if (legacySummary != null && isRecord(legacySummary)) {
    const s = legacySummary
    return {
      subTotal: num(s.sub_total),
      totalTax: num(s.total_tax),
      totalFees: num(s.total_fees),
      totalDiscount: num(s.total_discount),
      payableAmount: num(s.payable_amount),
      taxes: [],
      fees: mapLegacyChargesList(data.appliedChargesList),
      discounts: mapLegacyDiscountList(data.appliedDiscountList),
      savingsSummary,
      ...(discountEvaluations !== undefined ? { discountEvaluations } : {}),
    }
  }

  return {
    subTotal: num(data.sub_total),
    totalTax: num(data.total_tax),
    totalFees: num(data.total_fees),
    totalDiscount: num(data.total_discount),
    payableAmount: num(data.payable_amount),
    taxes: mapTaxRows(data.taxes),
    fees: mapFeeRows(data.fees),
    discounts: mapDiscountRows(data.discounts),
    savingsSummary,
    ...(discountEvaluations !== undefined ? { discountEvaluations } : {}),
  }
}

/**
 * Maps the full HTTP JSON body returned by `API.post` for billing preview.
 * Expects `{ data: <payload> }` after axios unwrap (i.e. pass `response.data` from axios).
 */
export function mapBillingPreviewResponse(
  apiResponse: BillingPreviewHttpEnvelope | null | undefined
): BillingPreviewUI {
  if (!apiResponse?.data) {
    throw new Error("Invalid billing preview response")
  }
  return mapBillingPreviewApiPayloadToUI(apiResponse.data)
}